import {
  MouseEventHandler,
  forwardRef,
  ForwardedRef,
  useRef,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useHistory } from 'react-router-dom';
import cn from 'classnames';
import { Playlist } from '@raydiant/api-client-js';
import Spinner from '@raydiant/raydial/components/Spinner';
import Text from '@raydiant/raydial/components/Text';
import AlertIcon from 'raydiant-elements/core/AlertIcon';
import playlistIcon from '../../../../LibraryPage/v2/assets/playlist-icon.svg';
import { ItemIDPath, PlaylistIDPath } from '../../../playlistPageTypes';
import PlaylistItemListInternalV2 from './PlaylistItemListInternalV2';
import PlaylistItemDropIndicatorV2 from './PlaylistItemDropIndicatorV2';
import usePlaylistPagePlaylist from '../../../usePlaylistPagePlaylist';
import PlaylistItemPlaylistActionsV2 from './PlaylistItemPlaylistActionsV2';
import usePlaylistPageContext from '../../../usePlaylistPageContext';
import PlaylistAddButtonV2 from './PlaylistAddButtonV2';
import RecentlyAddedIndicatorV2 from './RecentlyAddedIndicatorV2';
import { serializeIDPath } from '../../../usePlaylistPageState/utilities';
import * as paths from '../../../../../routes/paths';

interface PlaylistItemPlaylistV2Props {
  className?: string;
  playlist: Playlist;
  indent?: number;
  expanded?: boolean;
  cyclic?: boolean;
  selected?: boolean;
  editable?: boolean;
  disabled?: boolean;
  dropPosition?: 'top' | 'bottom' | 'center';
  itemIdPath: ItemIDPath;
  playlistIdPath: PlaylistIDPath;
  hideActions?: boolean;
  onClick?: MouseEventHandler;
  onEditClick?: MouseEventHandler;
  editDisabled?: boolean;
  editDisabledTooltip?: string;
  onToggleExpandClick?: MouseEventHandler<HTMLButtonElement>;
  hasSchedule?: boolean;
  hasCompletedSchedule?: boolean;
  onScheduleClick?: MouseEventHandler<HTMLButtonElement>;
  onRenameClick?: () => void;
  onRulesClick?: () => void;
  onTagsClick?: () => void;
  onDuplicateClick?: () => void;
  duplicateDisabled?: boolean;
  duplicateDisabledTooltip?: string;
  onSeeInLibraryClick?: () => void;
  seeInLibraryDisabled?: boolean;
  onRemoveClick?: () => void;
  showRulesAndTags?: boolean;
  isRecentlyAdded?: boolean;
}

const PlaylistItemPlaylistV2 = forwardRef<
  HTMLDivElement,
  PlaylistItemPlaylistV2Props
>(
  (
    {
      playlist,
      className,
      indent = 0,
      expanded = false,
      cyclic = false,
      selected = false,
      editable = false,
      disabled = false,
      dropPosition,
      itemIdPath,
      playlistIdPath,
      hideActions = false,
      onClick,
      onEditClick,
      editDisabled = false,
      editDisabledTooltip,
      onToggleExpandClick,
      hasSchedule = false,
      hasCompletedSchedule = false,
      onScheduleClick,
      onRenameClick,
      onRulesClick,
      onTagsClick,
      onDuplicateClick,
      duplicateDisabled = false,
      duplicateDisabledTooltip,
      onSeeInLibraryClick,
      seeInLibraryDisabled = false,
      onRemoveClick,
      showRulesAndTags,
      isRecentlyAdded = false,
    },
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const history = useHistory();

    const {
      queryParams,
      getPageUrl,
      updatePlaylist,
      resetEditItemName,
      getItemError,
      getPlaylistNameError,
      getPlaylistStartDatetimeError,
      getPlaylistEndDatetimeError,
    } = usePlaylistPageContext();

    // Fetch playlist items when expanded (if not already fetched).
    const { status, data: pagePlaylist } = usePlaylistPagePlaylist(
      playlist.id,
      {
        enabled: expanded,
      },
    );

    // State

    const [name, setName] = useState(playlist.name);

    // Refs

    const nameRef = useRef<HTMLInputElement | null>(null);

    // Callbacks

    const handleNameBlur = useCallback(() => {
      updatePlaylist(playlist.id, { name });
      resetEditItemName();
    }, [updatePlaylist, resetEditItemName, playlist.id, name]);

    const selectLibraryItems = useCallback(() => {
      // Add items inside this playlist by using the playlist ID directly.
      const pageUrl = getPageUrl({
        ...queryParams,
        addItemsAfterPath: serializeIDPath(itemIdPath),
        addToPlaylist: true,
        addToPlaylistId: playlist.id,
      });

      history.push(
        paths.presentations({
          folderId: queryParams.folderId, // Preserve folder context so the picker opens in the same folder
          selectionId: 'PlaylistPage',
          backTo: pageUrl,
          addToLabel: playlist.name,
          saveTo: pageUrl,
          sessionId: queryParams.sessionId,
          addToPlaylistId: playlist.id,
          scope: queryParams.scope, // Propagate library scope (owned/shared/shared_library) to the add-content modal
        }),
      );
    }, [playlist, queryParams, history, getPageUrl, itemIdPath]);

    // Effects

    // Update state when playlist changes.
    useEffect(() => {
      setName(playlist.name);
    }, [playlist.name]);

    // Select text when editable changes from false to true.
    useEffect(() => {
      if (editable) {
        setTimeout(() => {
          if (!nameRef.current) return;
          nameRef.current.focus();
          nameRef.current.select();
        });
      }
    }, [editable]);

    // Render

    const showError =
      getItemError(itemIdPath) ||
      getPlaylistNameError(playlist.id) ||
      getPlaylistStartDatetimeError(playlist.id) ||
      getPlaylistEndDatetimeError(playlist.id);

    const shouldExpand = expanded && status !== 'loading';

    return (
      <div ref={ref} className="relative pb-0.5">
        {dropPosition && dropPosition !== 'center' && (
          <PlaylistItemDropIndicatorV2 position={dropPosition} />
        )}
        {isRecentlyAdded && <RecentlyAddedIndicatorV2 />}
        <div
          className={cn(
            'rounded-lg border-2',
            selected ? '!border-blue-600' : 'border-gray-100',
            dropPosition === 'center' && 'shadow-[inset_0px_0px_0px_2px_#000]',
          )}
        >
          {/* Playlist header row */}
          <div
            className={cn(
              'relative group/item flex flex-row h-12 items-center px-3 shrink-0 justify-center',
              shouldExpand && 'border-b border-gray-100',
              selected && 'cursor-pointer',
              disabled && 'bg-[#eeeeee] text-[#999999]',
              className,
            )}
            onClick={onClick}
          >
            {showError && (
              <AlertIcon className="absolute top-[5px] left-[6px] h-[15px] w-[15px] z-[1]" />
            )}

            <div className="flex items-center gap-2 overflow-hidden">
              <div className="flex items-center justify-center h-6 w-6 shrink-0 relative rounded-md overflow-hidden">
                {status === 'loading' ? (
                  <Spinner className="size-4" />
                ) : (
                  <div
                    className="w-6 h-6 rounded-md bg-gray-200 bg-center bg-no-repeat bg-cover"
                    style={{ backgroundImage: `url(${playlistIcon})` }}
                  />
                )}
              </div>
              <div
                className={cn(
                  'flex items-center min-w-0 overflow-hidden',
                  disabled && 'text-inherit',
                )}
              >
                {editable ? (
                  <input
                    ref={nameRef}
                    className="bg-transparent outline-none border-none w-full text-base text-black"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={handleNameBlur}
                  />
                ) : (
                  <Text
                    variant="body"
                    className="font-semibold text-black block truncate"
                    truncate
                  >
                    {name}
                  </Text>
                )}
              </div>
            </div>

            <div className="flex-1" />

            {!hideActions && (
              <div className="h-full flex items-center justify-end min-w-[126px]">
                <PlaylistItemPlaylistActionsV2
                  expanded={shouldExpand}
                  expandedDisabled={cyclic}
                  hasSchedule={hasSchedule}
                  hasCompletedSchedule={hasCompletedSchedule}
                  onEditClick={onEditClick}
                  editDisabled={editDisabled}
                  editDisabledTooltip={editDisabledTooltip}
                  onToggleExpandClick={onToggleExpandClick}
                  onScheduleClick={onScheduleClick}
                  onRenameClick={onRenameClick}
                  onRulesClick={onRulesClick}
                  onTagsClick={onTagsClick}
                  onDuplicateClick={onDuplicateClick}
                  duplicateDisabled={duplicateDisabled}
                  duplicateDisabledTooltip={duplicateDisabledTooltip}
                  onSeeInLibraryClick={onSeeInLibraryClick}
                  seeInLibraryDisabled={seeInLibraryDisabled}
                  onRemoveClick={onRemoveClick}
                  showRulesAndTags={showRulesAndTags}
                />
              </div>
            )}
          </div>

          {/* Expanded children inside the card */}
          {!cyclic && shouldExpand && (
            <div className="px-3 pb-3">
              <PlaylistAddButtonV2
                onClick={selectLibraryItems}
                isDisabled={disabled}
              />
              {pagePlaylist && pagePlaylist.items.length > 0 && (
                <PlaylistItemListInternalV2
                  playlist={pagePlaylist}
                  itemIdPath={itemIdPath}
                  playlistIdPath={playlistIdPath}
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default PlaylistItemPlaylistV2;
